import { FC } from 'react';
import KpiCard from '@/components/admin/KpiCard';
import type { KpisQualidade as Kpis, ResumoReprocesso } from '@/utils/equiplexQualidade';
import { fmtCompacto, fmtDias, fmtPct, fmtUn } from '@/utils/painelFormat';
import LinhaRegua from '../LinhaRegua';

interface Props {
  kpis: Kpis;
  /** Lotes reprocessados nos meses escolhidos; `null` para frasco ou snapshot sem o dado. */
  reprocesso: ResumoReprocesso | null;
  /** Prazo de liberação do tipo escolhido (14 no produto acabado); `null` = ainda sem prazo (frasco). */
  prazoLiberacao?: number | null;
  /** O snapshot já tem previsão e desvio (16/10/2026); antes disso o card não aparece. */
  temPrevisao?: boolean;
  /** Leva para a tabela filtrada nos lotes que passaram da previsão. */
  onAtrasados?: () => void;
}

/** Dos lotes que saíram do CQ, quantos dentro da previsão; e quantos da fila já passaram dela. */
const CardPrevisao: FC<{ kpis: Kpis; prazo: number | null; onAtrasados?: () => void }> = ({ kpis, prazo, onAtrasados }) => {
  if (prazo === null) {
    return (
      <KpiCard icon="event_available" value="—" label="Liberados no prazo" sublabel="frasco: prazo de liberação ainda a definir">
        <LinhaRegua>A previsão e o desvio aparecem quando a Qualidade definir o prazo do frasco.</LinhaRegua>
      </KpiCard>
    );
  }
  return (
    <KpiCard
      icon="event_available"
      value={fmtPct(kpis.pctNoPrazo)}
      label={`Liberados em até ${prazo} dias`}
      sublabel={`${fmtUn(kpis.saidasNoPrazo)} de ${fmtUn(kpis.saidasComPrevisao)} lotes que saíram do CQ`}
    >
      <LinhaRegua>Previsão de liberação = entrada no CQ + {prazo} dias. Desvio = passou ou não da previsão.</LinhaRegua>
      {kpis.filaAtrasados > 0 && (
        <button
          type="button"
          onClick={onAtrasados}
          data-testid="qualidade-ver-atrasados"
          className="self-start text-xs font-medium text-content-primary underline hover:text-content-secondary"
        >
          {fmtUn(kpis.filaAtrasados)} {kpis.filaAtrasados === 1 ? 'lote na fila já passou' : 'lotes na fila já passaram'} da previsão
        </button>
      )}
    </KpiCard>
  );
};

/** Os números do recorte. A fila é "agora" (até a última carga), os demais são dos meses escolhidos. */
const KpisQualidade: FC<Props> = ({ kpis, reprocesso, prazoLiberacao = null, temPrevisao = false, onAtrasados }) => (
  <div
    className="grid grid-cols-[repeat(auto-fit,minmax(15rem,1fr))] gap-4"
    data-testid="qualidade-kpis"
  >
    <KpiCard
      icon="hourglass_top"
      value={fmtUn(kpis.filaTotal)}
      label="Aguardando CQ agora"
      sublabel={
        kpis.filaForaDaJanela
          ? `${fmtCompacto(kpis.filaQuantidade)} un · ${fmtUn(kpis.filaForaDaJanela)} com entrada antiga`
          : `${fmtCompacto(kpis.filaQuantidade)} unidades no CQ`
      }
    >
      <LinhaRegua>
        Lotes com saldo no depósito do CQ (CQ e CQV), como no CE0811. Ficha aberta sem saldo não conta: está na lista de fichas
        a encerrar.
      </LinhaRegua>
    </KpiCard>
    <KpiCard
      icon="timer"
      value={fmtDias(kpis.prazoMedio)}
      label="Prazo médio de liberação"
      sublabel={`mediana ${fmtDias(kpis.prazoMediana)} · 9 em 10 lotes em até ${fmtDias(kpis.prazoP90)}`}
    />
    {temPrevisao && <CardPrevisao kpis={kpis} prazo={prazoLiberacao} onAtrasados={onAtrasados} />}
    <KpiCard icon="inventory_2" value={fmtUn(kpis.lotes)} label="Entrou no CQ" sublabel={`lotes · ${fmtCompacto(kpis.quantidade)} unidades`}>
      <LinhaRegua>
        Pela data da ficha de CQ (o lote entrando no estoque, depois do reporte da OP). O produzido das outras abas é pela
        data de início da OP, então no mesmo período os dois não batem.
      </LinhaRegua>
    </KpiCard>
    <KpiCard
      icon="check_circle"
      value={fmtPct(kpis.pctLiberados)}
      label="Liberados"
      sublabel={`${fmtUn(kpis.liberados)} lotes${kpis.condicionais ? ` · ${fmtUn(kpis.condicionais)} com aprovação condicional` : ''}`}
    />
    <KpiCard
      icon="report"
      value={fmtUn(kpis.rejeicaoPendente)}
      label="Rejeição sem destino"
      sublabel="inspecionados, com quantidade rejeitada esperando decisão"
    />
    {reprocesso ? (
      <KpiCard
        icon="autorenew"
        value={fmtUn(reprocesso.lotes)}
        label="Lotes reprocessados"
        sublabel={
          reprocesso.pct === null
            ? 'sem lote produzido no período'
            : `${fmtPct(reprocesso.pct)} dos ${fmtUn(reprocesso.produzidos)} lotes produzidos · ${fmtCompacto(reprocesso.quantidade)} un`
        }
      >
        <LinhaRegua>Pela data de início da OP de reprocesso (100…), com o reporte dela, como o produzido. Só produto acabado.</LinhaRegua>
      </KpiCard>
    ) : (
      <KpiCard icon="rule" value={fmtUn(kpis.condicionais)} label="Liberações condicionais" sublabel="aprovação com ressalva" />
    )}
  </div>
);

export default KpisQualidade;
